import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["autoFocus", "disabled", "error", "format", "iconSize", "Input", "id", "inputRef", "inputWidth", "interval", "max", "min", "name", "onBlur", "onChange", "onFocus", "onKeyDown", "onOpenChange", "open", "readOnly", "use", "value", "cssStyles"];
import I18n from 'I18n';
import { Component } from 'react';
import memoize from 'react-utils/memoize';
import { NumberInput } from '../NumberInput';
import { uniqueId } from '../../utils/uniqueId';
import { formatValue, parseValue } from './TimeInput.utils';
import { syntheticEvent } from '../../utils/SyntheticEvent';
import { callIfPossible } from '../../utils/functions';
import invariant from 'react-utils/invariant';
import { FieldsetContextConsumer } from '../../context/FieldsetContext';
import { Popover } from '../Popover';
import { InputIconWrapper } from '../Dates/internal/InputIconWrapper';
import ShareInput from '../ShareInput/ShareInput';
import { Controllable } from '../Controllable';
import { PrivateTimePickerOptions } from './PrivateTimePickerOptions';
import { withTokenProps } from '../../../utils/withTokenProps';
import { TimeIcon } from 'foundations-assets/icons/components/TimeIcon';
import { jsx as _jsx } from "react/jsx-runtime";
export const MAX_TIME_OF_DAY = 1439; // 24 * 60 - 1

const roundToNearest = (value, step, round = Math.round) => round(value / step) * step;
const getLocalizedTimeFormat = memoize(() => 'HH:MM'.replace(/HH/, I18n.text('ui.timePicker.placeholder.hour')).replace(/MM/, I18n.text('ui.timePicker.placeholder.minute')));
class TimeInputComponent extends Component {
  static getDerivedStateFromProps(props, state) {
    const isOpening = !state.prevOpen && props.open;
    if (isOpening) {
      return {
        activeDescendant: props.value,
        prevOpen: props.open
      };
    }
    if (state.prevOpen !== props.open) {
      return {
        prevOpen: props.open
      };
    }
    return null;
  }
  constructor(props) {
    super(props);
    this.numberInputFormatter = value => {
      const format = this.props.format || 'LT';
      return value !== undefined ? formatValue(format, value) : '';
    };
    this.numberInputParser = rawValue => {
      const format = this.props.format || 'LT';
      const parsedValue = parseValue(format, rawValue);
      return parsedValue !== undefined ? parsedValue : null;
    };
    this.makeListboxOptionId = value => {
      return `${this._listboxOptionIdPrefix}-${value}`;
    };
    this.handleOptionClick = event => {
      var _inputRef$current;
      const {
        inputRef
      } = this.props;
      event.preventDefault();
      callIfPossible(this.props.onChange, syntheticEvent(event.target.value));
      inputRef === null || inputRef === void 0 || (_inputRef$current = inputRef.current) === null || _inputRef$current === void 0 || _inputRef$current.blur();
    };
    this.handleOptionHover = value => {
      this.setActiveDescendant(value);
    };
    this.handleInputFocus = evt => {
      var _inputRef$current2;
      const {
        inputRef,
        onFocus,
        onOpenChange
      } = this.props;
      if (onOpenChange) onOpenChange(syntheticEvent(true));
      inputRef === null || inputRef === void 0 || (_inputRef$current2 = inputRef.current) === null || _inputRef$current2 === void 0 || _inputRef$current2.select();
      if (onFocus) onFocus(evt);
    };
    this.handleInputBlur = evt => {
      callIfPossible(this.props.onOpenChange, syntheticEvent(false));
      callIfPossible(this.props.onBlur, evt);
    };
    this.handleInputChange = evt => {
      this.setActiveDescendant(Number(evt.target.value));
      callIfPossible(this.props.onChange, evt);
    };
    this.handleInputKeyDown = evt => {
      const {
        value,
        interval = 30,
        min = 0,
        max = MAX_TIME_OF_DAY,
        onChange,
        open = false,
        onOpenChange,
        onKeyDown,
        disabled,
        readOnly
      } = this.props;
      if (disabled || readOnly) {
        return;
      }
      const {
        activeDescendant
      } = this.state;
      const firstAvailableOption = min;
      const lastAvailableOption = max;
      let newValue = value;
      let newOpen = open;
      let newActiveDescendant = activeDescendant;
      switch (evt.key) {
        case 'ArrowDown':
          evt.preventDefault(); // Don't move the cursor
          newOpen = true;
          // Increment or default to first option in list
          newActiveDescendant = activeDescendant == null ? firstAvailableOption : Math.min(lastAvailableOption, activeDescendant + interval);
          newActiveDescendant = roundToNearest(newActiveDescendant, interval, Math.floor);
          break;
        case 'ArrowUp':
          evt.preventDefault(); // Don't move the cursor
          newOpen = true;
          // Decrement or default to last option in list
          newActiveDescendant = activeDescendant == null ? lastAvailableOption : Math.max(firstAvailableOption, activeDescendant - interval);
          newActiveDescendant = roundToNearest(newActiveDescendant, interval, Math.ceil);
          break;
        case 'Enter':
          // Set activeDescendant as new value on selection
          if (open) {
            newValue = activeDescendant;
          }
          newOpen = !open;
          break;
        default:
          callIfPossible(onKeyDown, evt);
          return;
      }
      if (newActiveDescendant !== undefined && activeDescendant !== newActiveDescendant) {
        this.setActiveDescendant(newActiveDescendant);
      }
      if (value !== newValue) {
        callIfPossible(onChange, syntheticEvent(newValue));
      }
      if (open !== newOpen) {
        callIfPossible(onOpenChange, syntheticEvent(newOpen));
      }
    };
    this.state = {
      activeDescendant: props.value,
      popoverWidth: 'auto',
      prevOpen: props.open
    };
    this._listboxOptionIdPrefix = `UITimeInput-${uniqueId()}-result-item`;
  }
  componentDidMount() {
    this.updatePopoverContentWidth();
  }
  componentDidUpdate(prevProps) {
    const isOpening = !prevProps.open && this.props.open;
    if (isOpening) {
      this.updatePopoverContentWidth();
    }
  }

  /** @private */

  /** @private */

  setActiveDescendant(value) {
    this.setState({
      activeDescendant: value
    });
  }
  updatePopoverContentWidth() {
    if (this.props.inputRef && this.props.inputRef.current) {
      const inputEl = this.props.inputRef.current;
      const {
        width: exactWidth
      } = inputEl.getBoundingClientRect();
      // @ts-ignore TODO: figure this out later
      const roundedWidth = window.chrome ? Math.floor(exactWidth) : exactWidth;
      this.setState({
        popoverWidth: `${roundedWidth}px` // account for 1px border added by UIPopover
      });
    }
  }
  render() {
    const _this$props = this.props,
      {
        autoFocus,
        disabled,
        error,
        format: __format = 'LT',
        iconSize,
        Input = NumberInput,
        id,
        inputRef,
        inputWidth,
        interval = 30,
        max = MAX_TIME_OF_DAY,
        min = 0,
        name,
        onOpenChange: onPopoverOpenChange,
        open: popoverOpen = false,
        readOnly,
        use = 'default',
        value: normalizedValue,
        cssStyles
      } = _this$props,
      rest = _objectWithoutPropertiesLoose(_this$props, _excluded);
    const {
      activeDescendant
    } = this.state;
    if (process.env.NODE_ENV !== 'production') {
      invariant(min >= 0, 'UITimeInput: `min` should be a positive integer (received %s)', min);
      invariant(max >= 0, 'UITimeInput: `max` should be a positive integer (received %s)', max);
      invariant(max <= MAX_TIME_OF_DAY, 'UITimeInput: `max` should not be greater than 11:59 PM (received %s > %s)', max, MAX_TIME_OF_DAY);
      invariant(min < max, 'UITimeInput: `min` should not be greater than `max` (received %s > %s)', min, max);
    }
    const computedPopoverOpen = popoverOpen && !disabled && !readOnly;
    return /*#__PURE__*/_jsx(FieldsetContextConsumer, {
      children: fieldsetContext => {
        const computedDisabled = disabled || fieldsetContext.disabled;
        // If fieldsetContext.size is 'small', icon should be 14x14, otherwise 16x16
        const computedIconSize = iconSize || (fieldsetContext.size === 'small' ? '200' : '300');
        return /*#__PURE__*/_jsx(Popover, {
          arrowSize: "none",
          placement: "bottom right",
          inlineSize: this.state.popoverWidth,
          open: computedPopoverOpen,
          onOpenChange: onPopoverOpenChange,
          content: /*#__PURE__*/_jsx(PrivateTimePickerOptions, {
            focusedValue: activeDescendant,
            interval: interval,
            makeOptionId: this.makeListboxOptionId,
            max: max,
            min: min,
            onOptionClick: this.handleOptionClick,
            onOptionHover: this.handleOptionHover,
            valueFormatter: this.numberInputFormatter
          }),
          children: /*#__PURE__*/_jsx(InputIconWrapper, Object.assign({}, rest, {
            disabled: computedDisabled,
            icon: TimeIcon,
            size: computedIconSize,
            $cssStyles: cssStyles,
            children: /*#__PURE__*/_jsx(Input, {
              "aria-activedescendant": activeDescendant ? this.makeListboxOptionId(activeDescendant) : undefined,
              autoComplete: "off",
              autoFocus: autoFocus,
              disabled: computedDisabled,
              error: error,
              formatter: this.numberInputFormatter,
              id: id,
              inlineSize: inputWidth ? `${inputWidth}px` : undefined,
              inputRef: inputRef,
              max: max,
              min: min,
              name: name,
              onBlur: this.handleInputBlur,
              onChange: this.handleInputChange,
              onFocus: this.handleInputFocus,
              onKeyDown: this.handleInputKeyDown,
              parser: this.numberInputParser,
              placeholder: getLocalizedTimeFormat(),
              readOnly: readOnly,
              use: use,
              value: normalizedValue
            })
          }))
        });
      }
    });
  }
}
TimeInputComponent.defaultProps = {
  min: 0,
  max: MAX_TIME_OF_DAY,
  format: 'LT',
  Input: NumberInput,
  use: 'default',
  interval: 30,
  open: false
};
export const TimeInput = ShareInput(Controllable(withTokenProps(TimeInputComponent), ['open', 'value']));
TimeInput.displayName = 'TimeInput';